/**
 * PageSuggestionBubble — V3 Page 07
 *
 * Friendly agent-voice prose bubble that appears after an
 * ArtifactPreviewCard: "Want me to pin this? It will land under X."
 * Tone calibrated to MM-PORTAL-DNA-MIRROR §7 (SMS-reply friendly).
 *
 * Page 07 spec §data-bindings lists this as a distinct WS frame
 * (`page_suggestion`); the orchestrator emits it atop the `done` envelope
 * whenever `CHAT_PIN_SIDEBAR=1` AND the turn classified as an
 * artifact-producing intent.
 *
 * The bubble is read-only — clicking "Pin" is handled on the card itself,
 * not here, so the two components stay uncoupled (card can appear without
 * the bubble, bubble can appear without the card).
 */

import { clsx } from 'clsx'
import { Sparkles } from 'lucide-react'

export interface PageSuggestion {
  prompt: string
  cta_label?: string
  explicit_pin?: boolean
  suggested_label?: string
  suggested_group?: string
}

interface Props {
  suggestion: PageSuggestion
  /** Optional: when present the bubble becomes a simple visual prompt; the
   *  actual pin action lives on the ArtifactPreviewCard and is not
   *  re-duplicated here. Kept as a prop so the bubble can be rendered
   *  standalone in stories / tests. */
  highlighted?: boolean
}

export function PageSuggestionBubble({ suggestion, highlighted = false }: Props) {
  return (
    <div
      role="note"
      aria-label="Pin suggestion"
      data-testid="page-suggestion-bubble"
      className={clsx(
        'mt-2 flex items-start gap-2 rounded-lg px-3 py-2',
        highlighted
          ? 'bg-carnival-100/50 border border-carnival-300/50'
          : 'bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700',
      )}
    >
      <Sparkles
        className={clsx(
          'mt-0.5 w-4 h-4 flex-shrink-0',
          highlighted ? 'text-carnival-600' : 'text-slate-400 dark:text-slate-500',
        )}
        aria-hidden
      />
      <p className="text-xs leading-relaxed text-slate-700 dark:text-slate-200">
        {renderPromptWithBold(suggestion.prompt)}
      </p>
    </div>
  )
}

/**
 * The composer emits prompts with `**bold**` markdown segments for
 * emphasis on the suggested group name. The bubble renders those as
 * semantic <strong> so screen readers announce emphasis. Simple splitter
 * instead of react-markdown — the prompt is bounded, authored server-side.
 */
function renderPromptWithBold(text: string) {
  const parts: React.ReactNode[] = []
  const re = /\*\*([^*]+)\*\*/g
  let lastIdx = 0
  let match: RegExpExecArray | null
  let key = 0
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<span key={key++}>{text.slice(lastIdx, match.index)}</span>)
    }
    parts.push(<strong key={key++} className="font-semibold">{match[1]}</strong>)
    lastIdx = match.index + match[0].length
  }
  if (lastIdx < text.length) {
    parts.push(<span key={key++}>{text.slice(lastIdx)}</span>)
  }
  return parts.length > 0 ? parts : text
}
